
import { supabase } from '@/lib/supabase';
import { TradingAccountOption } from '@/types/trader';
import { circuitBreaker, categorizeOAuthError, shouldRetry, getRetryDelay } from '@/utils/oauth-errors';

export interface AtomicConnectionRequest {
  cspAccountId: string;
  apiName: string;
  clientId: string;
  secretId: string;
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
  selectedAccount: TradingAccountOption;
}

interface NetworkConfig {
  timeout: number;
  maxRetries: number;
  retryDelay: number;
}

class NetworkResilientService {
  private config: NetworkConfig = {
    timeout: 30000, // 30 seconds
    maxRetries: 3,
    retryDelay: 1000
  };

  private connectionQuality: 'good' | 'poor' | 'offline' = 'good';
  private lastConnectivityCheck = 0;
  private connectivityCheckInterval = 60000; // 1 minute

  constructor() {
    this.detectConnectionQuality();
    
    // Monitor online/offline status
    window.addEventListener('online', () => {
      this.connectionQuality = 'good';
      circuitBreaker.reset();
    });
    
    window.addEventListener('offline', () => {
      this.connectionQuality = 'offline';
    });
  }

  private async detectConnectionQuality(): Promise<void> {
    const now = Date.now();
    if (now - this.lastConnectivityCheck < this.connectivityCheckInterval) {
      return;
    }

    this.lastConnectivityCheck = now;

    try {
      const startTime = performance.now();
      
      // Test with a small, fast endpoint
      const response = await fetch('https://httpbin.org/status/200', {
        method: 'HEAD',
        cache: 'no-cache'
      });
      
      const endTime = performance.now();
      const latency = endTime - startTime;

      if (response.ok) {
        this.connectionQuality = latency > 2000 ? 'poor' : 'good';
      } else {
        this.connectionQuality = 'poor';
      }
    } catch {
      this.connectionQuality = 'offline';
    }
  }

  private getTimeoutForQuality(): number {
    switch (this.connectionQuality) {
      case 'good': return this.config.timeout;
      case 'poor': return this.config.timeout * 2;
      case 'offline': return this.config.timeout * 3;
    }
  }

  private async fetchWithResilience(
    url: string, 
    options: RequestInit, 
    attemptNumber: number = 1
  ): Promise<Response> {
    await this.detectConnectionQuality();

    // Check circuit breaker
    if (!circuitBreaker.canExecute()) {
      throw new Error('Circuit breaker open - service temporarily unavailable');
    }

    const timeout = this.getTimeoutForQuality();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        (error as any).status = response.status;
        throw error;
      }

      // Record success for circuit breaker
      circuitBreaker.recordSuccess();
      return response;

    } catch (error: any) {
      clearTimeout(timeoutId);

      const categorizedError = categorizeOAuthError(error);
      circuitBreaker.recordFailure();

      // Check if we should retry
      if (shouldRetry(categorizedError, attemptNumber) && attemptNumber < this.config.maxRetries) {
        const delay = getRetryDelay(attemptNumber, categorizedError.category);
        
        console.warn(`Request failed (attempt ${attemptNumber}), retrying in ${delay}ms:`, error);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.fetchWithResilience(url, options, attemptNumber + 1);
      }

      throw error;
    }
  }

  async post(url: string, data: any): Promise<any> {
    const response = await this.fetchWithResilience(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    return response.json();
  }

  async get(url: string, headers?: Record<string, string>): Promise<any> {
    const response = await this.fetchWithResilience(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...headers
      }
    });

    return response.json();
  }

  getConnectionQuality(): string {
    return this.connectionQuality;
  }

  getConfig(): NetworkConfig {
    return { ...this.config };
  }

  updateConfig(newConfig: Partial<NetworkConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

const networkService = new NetworkResilientService();

export const apiConnectionService = {
  // Atomic connection method with network resilience
  async createAtomicConnection({
    cspAccountId,
    apiName,
    clientId,
    secretId,
    accessToken,
    refreshToken,
    expiresIn,
    selectedAccount
  }: AtomicConnectionRequest) {
    const apiConnectionId = crypto.randomUUID();
    
    // Calculate expire_date based on expires_in (seconds from now)
    const expireDate = expiresIn 
      ? new Date(Date.now() + expiresIn * 1000).toISOString()
      : null;

    try {
      // Use RPC with exact parameter order matching the function
      const { error } = await supabase.rpc('create_api_connection', {
        p_api_connection_id: apiConnectionId,
        p_csp_account_id: cspAccountId,
        p_api_name: apiName,
        p_client_id: clientId,
        p_secret_id: secretId,
        p_access_token: accessToken,
        p_account_number: selectedAccount.accountNumber,
        p_account_id: selectedAccount.id,
        p_balance: selectedAccount.balance,
        p_is_live: selectedAccount.isLive,
        p_refresh_token: refreshToken || null,
        p_expires_in: expiresIn || null,
        p_expire_date: expireDate,
        p_status: 'connected'
      });

      if (error) {
        circuitBreaker.recordFailure();
        throw error;
      }

      circuitBreaker.recordSuccess();
      return { apiConnectionId };

    } catch (error) {
      const categorizedError = categorizeOAuthError(error);
      throw new Error(`Connection failed: ${categorizedError.userMessage}`);
    }
  },

  // Enhanced admin credentials fetching with network resilience
  async getAdminCredentials() {
    try {
      const result = await networkService.get(
        'https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/get-admin-credentials'
      );

      if (!result.success || !result.client_id) {
        throw new Error('Invalid response from admin credentials endpoint');
      }

      return {
        client_id: result.client_id,
        client_secret: 'admin-managed'
      };
    } catch (error) {
      const categorizedError = categorizeOAuthError(error);
      throw new Error(`Admin credentials fetch failed: ${categorizedError.userMessage}`);
    }
  },

  // Enhanced trading accounts fetching with network resilience
  async getTradingAccounts(accessToken: string) {
    try {
      const result = await networkService.get(
        'https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/ctrader-accounts',
        {
          'Authorization': `Bearer ${accessToken}`
        }
      );

      if (!result.data || !Array.isArray(result.data)) {
        throw new Error('Invalid response format from cTrader API');
      }

      return result.data;
    } catch (error) {
      const categorizedError = categorizeOAuthError(error);
      throw new Error(`Trading accounts fetch failed: ${categorizedError.userMessage}`);
    }
  },

  // Network diagnostics
  getNetworkStatus() {
    return {
      quality: networkService.getConnectionQuality(),
      circuitBreakerState: circuitBreaker.getState(),
      config: networkService.getConfig()
    };
  },

  // Utility method for cleanup if needed
  async cleanupOrphanedConnections(olderThanHours: number = 1) {
    const cutoffTime = new Date(Date.now() - olderThanHours * 60 * 60 * 1000).toISOString();
    
    try {
      const { error } = await supabase
        .from('api_connections')
        .delete()
        .eq('status', 'pending')
        .lt('created_at', cutoffTime);
        
      if (error) throw error;
      return { success: true };
    } catch (error) {
      const categorizedError = categorizeOAuthError(error);
      throw new Error(`Cleanup failed: ${categorizedError.userMessage}`);
    }
  }
};
